import { ApiError } from '../services/apiClient';

/**
 * Error copy for the operator screen.
 *
 * Unlike the join flow, the backend's 400 messages are worth showing here:
 * they are the validation rules of the field the operator just typed into
 * ("tenantId must be 2 to 32 lower-case letters..."). Everything else gets
 * written copy, because a framework message is not an explanation.
 */
export function adminErrorCopy(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Something went wrong. Please try again.';
  }
  switch (error.status) {
    case 0:
      return error.message;
    case 400:
      return error.message;
    case 401:
      return 'Your session ended. Sign in again.';
    case 403:
      return 'Your account is not allowed to do that.';
    case 404:
      return 'That tenant no longer exists. Refresh the list.';
    case 409:
      return error.message;
    case 501:
      return 'This deployment does not support that setting.';
    case 503:
      return 'The operator API is unavailable on this deployment.';
    default:
      return error.requestId
        ? `The server could not complete that (reference ${error.requestId}).`
        : 'The server could not complete that. Please try again.';
  }
}

/**
 * Error copy for the sign-in form, which needs different words from the panel
 * behind it.
 *
 * A wrong password and an unknown email get one message, on purpose: the form
 * must not be usable to find out which addresses have accounts here.
 */
export function loginErrorCopy(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Something went wrong. Please try again.';
  }
  switch (error.status) {
    case 0:
      return error.message;
    case 400:
      return error.message;
    case 401:
      // The backend sends one sentence for wrong credentials and another for a
      // disabled account, and both are safe to show: a disabled account's
      // owner already knows the address exists.
      return error.message;
    case 429:
      return `Too many sign-in attempts. Try again ${formatRetryAfter(
        error.retryAfterSeconds,
      )}.`;
    case 503:
      return 'Sign-in is switched off on this deployment.';
    default:
      return error.requestId
        ? `Sign-in is not available right now (reference ${error.requestId}).`
        : 'Sign-in is not available right now. Try again in a moment.';
  }
}

/** "in 30 seconds" / "in 5 minutes" / "in a few minutes" when unsaid. */
export function formatRetryAfter(seconds: number | undefined): string {
  if (seconds === undefined || seconds <= 0) {
    return 'in a few minutes';
  }
  if (seconds < 60) {
    return `in ${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) {
    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return 'in about an hour';
}
